import * as THREE from 'three';

/**
 * Build a sea mesh from OSM natural=coastline ways.
 *
 * OSM convention: following a coastline way, land is on the LEFT, sea on the RIGHT.
 * Algorithm:
 *   1. Stitch coastline way-segments into continuous chains.
 *   2. Clip each chain to the scene bbox.
 *   3. Build a THREE.Shape whose exterior is the CCW bbox (= sea fill), with
 *      each land area punched out as a CW hole.
 *      - Closed chain (island): reverse it → CW hole.
 *      - Open chain (mainland coast): close via CW traversal of bbox boundary.
 *
 * Shape coordinate space: (x, y) = (scene_x, −scene_z)
 *   so that rotateX(−π/2) lands geometry flat in the XZ plane.
 *
 * @param {Array}    coastlines  [{nodes:[{lat,lon}]}] from OSM parser
 * @param {Object}   proj        projection with .project(lat,lon)→{x,z}
 * @param {number}   radiusM     half-size of the loaded area in metres
 * @returns {THREE.Mesh|null}
 */
export function buildSeaMesh(coastlines, proj, radiusM) {
  if (!coastlines?.length) return null;

  const R = radiusM;

  // ── 1. Project ────────────────────────────────────────────────────────────
  const rawChains = coastlines.map(c =>
    c.nodes.map(n => proj.project(n.lat, n.lon)),
  );

  // ── 2. Stitch ─────────────────────────────────────────────────────────────
  const stitched = _stitchChains(rawChains);
  if (!stitched.length) return null;

  // ── 3. Sea shape: CCW bbox exterior in (x, -z) space ─────────────────────
  // SW(-R,-R) → SE(R,-R) → NE(R,R) → NW(-R,R)  → CCW ✓
  const shape = new THREE.Shape([
    new THREE.Vector2(-R, -R),
    new THREE.Vector2( R, -R),
    new THREE.Vector2( R,  R),
    new THREE.Vector2(-R,  R),
  ]);

  // ── 4. Land holes ─────────────────────────────────────────────────────────
  for (const raw of stitched) {
    const chain = _clipChain(raw, R);
    if (!chain || chain.length < 2) continue;
    const hole = _buildLandHole(chain, R);
    if (hole) shape.holes.push(hole);
  }

  // ── 5. Geometry ───────────────────────────────────────────────────────────
  const geo  = new THREE.ShapeGeometry(shape, 2);
  geo.rotateX(-Math.PI / 2);

  const mat  = new THREE.MeshLambertMaterial({ color: 0x1e5c80 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.position.y = 0.02; // above terrain baseline (-0.06) so sea is visible
  return mesh;
}

// ── Chain stitching ───────────────────────────────────────────────────────────

function _stitchChains(chains) {
  const EPS  = 0.5; // metres — close enough to stitch
  const todo = chains.map(c => [...c]);
  const done = [];

  while (todo.length) {
    let chain  = todo.shift();
    let merged = true;

    while (merged) {
      merged = false;
      for (let i = todo.length - 1; i >= 0; i--) {
        const s = todo[i];
        const cHead = chain[0], cTail = chain[chain.length - 1];
        const sHead = s[0],     sTail = s[s.length - 1];

        if (_d(cTail, sHead) < EPS) {
          chain = [...chain, ...s.slice(1)];
        } else if (_d(cTail, sTail) < EPS) {
          chain = [...chain, ...[...s].reverse().slice(1)];
        } else if (_d(cHead, sTail) < EPS) {
          chain = [...s, ...chain.slice(1)];
        } else if (_d(cHead, sHead) < EPS) {
          chain = [...[...s].reverse(), ...chain.slice(1)];
        } else {
          continue;
        }
        todo.splice(i, 1);
        merged = true;
        break;
      }
    }
    done.push(chain);
  }
  return done;
}

function _d(a, b) { return Math.hypot(a.x - b.x, a.z - b.z); }

// ── Bbox clipping (Liang–Barsky) ──────────────────────────────────────────────

function _clipChain(chain, R) {
  const EPS    = 0.5;
  const inside = p => p.x >= -R - EPS && p.x <= R + EPS
                   && p.z >= -R - EPS && p.z <= R + EPS;
  const result = [];

  for (let i = 0; i < chain.length; i++) {
    const p   = chain[i];
    const pin = inside(p);

    if (pin) {
      if (i > 0 && !inside(chain[i - 1])) {
        const inter = _segBboxIntersect(chain[i - 1], p, R);
        if (inter) result.push(inter);
      }
      result.push({ x: p.x, z: p.z });
    } else if (i > 0 && inside(chain[i - 1])) {
      const inter = _segBboxIntersect(chain[i - 1], p, R);
      if (inter) result.push(inter);
    }
  }

  // Chain passes through bbox without any node inside (e.g. very coarse OSM way)
  if (result.length === 0) {
    for (let i = 0; i < chain.length - 1; i++) {
      const inter = _segBboxIntersect(chain[i], chain[i + 1], R);
      if (inter) { result.push(inter); }
    }
    if (result.length < 2) return null;
    return [result[0], result[result.length - 1]];
  }

  return result.length >= 2 ? result : null;
}

/** Liang–Barsky: return the entry intersection of segment p1→p2 with bbox. */
function _segBboxIntersect(p1, p2, R) {
  const dx = p2.x - p1.x, dz = p2.z - p1.z;
  let t0 = 0, t1 = 1;

  const clip = (p, q) => {
    if (p < 0)      { const r = q / p; if (r > t1) return false; if (r > t0) t0 = r; }
    else if (p > 0) { const r = q / p; if (r < t0) return false; if (r < t1) t1 = r; }
    else if (q < 0) return false;
    return true;
  };

  if (!clip(-dx, p1.x + R) || !clip(dx, R - p1.x)) return null;
  if (!clip(-dz, p1.z + R) || !clip(dz, R - p1.z)) return null;
  if (t0 > 0) return { x: p1.x + t0 * dx, z: p1.z + t0 * dz };
  if (t1 < 1) return { x: p1.x + t1 * dx, z: p1.z + t1 * dz };
  return null;
}

// ── Land hole construction ────────────────────────────────────────────────────

function _buildLandHole(chain, R) {
  const first  = chain[0];
  const last   = chain[chain.length - 1];
  const closed = _d(first, last) < 10;

  // Shape space: (x, y) = (scene_x, -scene_z)
  const S = p => [p.x, -p.z];

  let pts;

  if (closed) {
    // Island: OSM chain winds CCW around land → reverse = CW hole
    pts = chain.map(S).reverse();
  } else {
    // Open/mainland coast: snap endpoints to bbox, close on the LAND side.
    // OSM: land is to the LEFT of travel direction (chain goes first→last).
    // CW hole must enclose the land: go via land-side bbox corners (entry→exit CW),
    // then trace the coast backwards (exit→entry) to close the land polygon.
    const entry = _snapToBbox(first, R);
    const exit  = _snapToBbox(last,  R);
    const landCorners = _cwBboxClosure(entry, exit, R);
    pts = [S(entry), ...landCorners.map(p => S(p)), S(exit),
           ...chain.slice(1, -1).reverse().map(S)];
  }

  if (pts.length < 3) return null;

  const path = new THREE.Path();
  path.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) path.lineTo(pts[i][0], pts[i][1]);
  path.closePath();
  return path;
}

// ── Bbox helpers ──────────────────────────────────────────────────────────────

/** Snap a point to the nearest edge of bbox [-R,R]². */
function _snapToBbox(p, R) {
  const x = Math.max(-R, Math.min(R, p.x));
  const z = Math.max(-R, Math.min(R, p.z));
  // Distances to each of the four edges
  const dN = Math.abs(z + R), dS = Math.abs(z - R);
  const dE = Math.abs(x - R), dW = Math.abs(x + R);
  const m  = Math.min(dN, dS, dE, dW);
  if (m === dN) return { x,  z: -R };
  if (m === dS) return { x,  z:  R };
  if (m === dE) return { x:  R, z };
  return { x: -R, z };
}

/**
 * Identify which bbox edge a boundary point lies on (scene coords).
 * Returns 0=north(z=-R), 1=east(x=+R), 2=south(z=+R), 3=west(x=-R), -1=none.
 */
function _bboxEdge(p, R) {
  const EPS = 2;
  if (Math.abs(p.z + R) < EPS) return 0; // north
  if (Math.abs(p.x - R) < EPS) return 1; // east
  if (Math.abs(p.z - R) < EPS) return 2; // south
  if (Math.abs(p.x + R) < EPS) return 3; // west
  return -1;
}

/** Normalised CW progress along a bbox edge [0,1]. */
function _bboxEdgeT(p, R, edge) {
  switch (edge) {
    case 0: return (p.x + R) / (2 * R);   // north  W→E
    case 1: return (p.z + R) / (2 * R);   // east   N→S
    case 2: return (R - p.x) / (2 * R);   // south  E→W
    case 3: return (R - p.z) / (2 * R);   // west   S→N
    default: return 0;
  }
}

/**
 * Walk CW along the bbox boundary (scene coords) from `from` to `to`,
 * returning intermediate corner points.
 *
 * CW traversal: N(W→E) → E(N→S) → S(E→W) → W(S→N)
 * End-of-edge corners: NE(R,-R), SE(R,R), SW(-R,R), NW(-R,-R)
 */
function _cwBboxClosure(from, to, R) {
  const corners = [
    { x:  R, z: -R }, // NE  (end of north edge)
    { x:  R, z:  R }, // SE  (end of east  edge)
    { x: -R, z:  R }, // SW  (end of south edge)
    { x: -R, z: -R }, // NW  (end of west  edge)
  ];

  const fEdge = _bboxEdge(from, R);
  const tEdge = _bboxEdge(to,   R);
  if (fEdge < 0 || tEdge < 0) return [];

  const fT = _bboxEdgeT(from, R, fEdge);
  const tT = _bboxEdgeT(to,   R, tEdge);

  const result = [];
  let edge = fEdge;

  for (let i = 0; i < 5; i++) {
    if (edge === tEdge) {
      // Same edge: stop if we're behind `to` in CW direction (first iteration only)
      if (i === 0 && fT <= tT) break;
      if (i  >  0)             break;
    }
    result.push({ ...corners[edge] });
    edge = (edge + 1) % 4;
  }

  return result;
}
